/**
 * MentionEditor — contentEditable-based composer with an inline @ dropdown.
 *
 * Storage model (props.value / onChange payload):
 *   value = Array<Node> where Node is one of:
 *     { type: "text", text: string }
 *     { type: "mention", node_id, subject_id, name }
 *     { type: "verse_card", node_id, reference, label, translation, text }
 *
 * The editor renders each Node as a DOM child of a contentEditable div and
 * serializes back to the array by walking children on each input event.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { resolveMention, createSubject } from "@/lib/api";

const uid = () => `n_${Math.random().toString(36).slice(2, 10)}`;

// ---------- render helpers ----------

function makeMentionElem(node) {
    const el = document.createElement("span");
    el.contentEditable = "false";
    el.className = "mention-chip";
    el.dataset.nodeType = "mention";
    el.dataset.nodeId = node.node_id;
    el.dataset.subjectId = node.subject_id;
    el.dataset.name = node.name;
    el.setAttribute("data-testid", `mention-chip-${node.node_id}`);
    el.textContent = `@${node.name}`;
    return el;
}

function makeVerseCardElem(node) {
    const el = document.createElement("span");
    el.contentEditable = "false";
    el.className = "verse-card-chip";
    el.dataset.nodeType = "verse_card";
    el.dataset.nodeId = node.node_id;
    el.dataset.reference = node.reference || (node.refs && node.refs[0]) || "";
    el.dataset.refs = JSON.stringify(node.refs || (node.reference ? [node.reference] : []));
    el.dataset.label = node.label;
    el.dataset.translation = node.translation || "kjv";
    el.dataset.text = node.text || "";
    el.dataset.format = node.format || "full";
    el.setAttribute("data-testid", `verse-card-${node.node_id}`);
    const showFull = (node.format || "full") === "full";
    el.innerHTML =
        `<span class="verse-card-label">${node.label}${node.translation ? " · " + node.translation.toUpperCase() : ""}</span>` +
        (showFull
            ? `<span class="verse-card-text scripture">${(node.text || "").replace(/</g, "&lt;")}</span>`
            : "");
    return el;
}

function serialize(root) {
    const out = [];
    const push = (n) => {
        if (n.type === "text") {
            const last = out[out.length - 1];
            if (last && last.type === "text") last.text += n.text;
            else out.push(n);
        } else {
            out.push(n);
        }
    };
    const walk = (node) => {
        for (const child of node.childNodes) {
            if (child.nodeType === Node.TEXT_NODE) {
                if (child.textContent) push({ type: "text", text: child.textContent });
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                if (child.tagName === "BR") {
                    push({ type: "text", text: "\n" });
                } else if (child.dataset && child.dataset.nodeType === "mention") {
                    push({
                        type: "mention",
                        node_id: child.dataset.nodeId,
                        subject_id: child.dataset.subjectId,
                        name: child.dataset.name,
                    });
                } else if (child.dataset && child.dataset.nodeType === "verse_card") {
                    let refs;
                    try { refs = JSON.parse(child.dataset.refs || "[]"); } catch { refs = []; }
                    if (!refs.length && child.dataset.reference) refs = [child.dataset.reference];
                    push({
                        type: "verse_card",
                        node_id: child.dataset.nodeId,
                        reference: child.dataset.reference,
                        refs,
                        label: child.dataset.label,
                        translation: child.dataset.translation,
                        text: child.dataset.text,
                        format: child.dataset.format || "full",
                    });
                } else {
                    // Unknown element (e.g. div wrappers from paste): walk into it
                    walk(child);
                }
            }
        }
    };
    walk(root);
    return out;
}

function hydrate(root, nodes) {
    root.innerHTML = "";
    for (const n of nodes || []) {
        if (n.type === "text") {
            // Split on newlines to render <br> for line breaks
            const parts = (n.text || "").split("\n");
            parts.forEach((p, i) => {
                if (p) root.appendChild(document.createTextNode(p));
                if (i < parts.length - 1) root.appendChild(document.createElement("br"));
            });
        } else if (n.type === "mention") {
            root.appendChild(makeMentionElem(n));
        } else if (n.type === "verse_card") {
            root.appendChild(makeVerseCardElem(n));
        }
    }
    // Ensure trailing text node so caret can rest
    if (!root.lastChild || root.lastChild.nodeType !== Node.TEXT_NODE) {
        root.appendChild(document.createTextNode(""));
    }
}

// ---------- Editor ----------

export default function MentionEditor({
    initialValue = [],
    onChange,
    space = "journal",   // "journal" | "notes"
    version = "kjv",
    placeholder = "Write when you're ready…",
    autoFocus = false,
    onSubjectCreated,
}) {
    const rootRef = useRef(null);
    const [dd, setDd] = useState(null); // {query, results, rect}
    const [activeIdx, setActiveIdx] = useState(0);
    const skipNextInput = useRef(false);
    const resolveSeq = useRef(0);

    // Hydrate once
    useEffect(() => {
        if (rootRef.current && rootRef.current.innerHTML === "") {
            hydrate(rootRef.current, initialValue);
            // Emit initial serialized value so parent sees any pre-inserted nodes
            if (initialValue && initialValue.length > 0) {
                onChange?.(serialize(rootRef.current));
            }
            if (autoFocus) {
                setTimeout(() => rootRef.current?.focus(), 40);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const emit = useCallback(() => {
        if (rootRef.current) onChange?.(serialize(rootRef.current));
    }, [onChange]);

    // ---------- @ detection ----------

    const detectAt = useCallback(() => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return null;
        const range = sel.getRangeAt(0);
        if (!range.collapsed) return null;
        const container = range.startContainer;
        if (container.nodeType !== Node.TEXT_NODE) return null;
        if (!rootRef.current?.contains(container)) return null;
        const offset = range.startOffset;
        const before = container.textContent.slice(0, offset);
        // Match @ followed by any non-@/newline chars up to caret (allows spaces
        // so phrase queries like "@white as snow" work). Stops at newline or another @.
        const m = before.match(/@([^@\n]{0,80})$/);
        if (!m) return null;
        const atIdx = before.lastIndexOf("@");
        const q = m[1];
        let rect;
        try {
            const r = document.createRange();
            r.setStart(container, atIdx);
            r.setEnd(container, offset);
            rect = r.getBoundingClientRect();
        } catch (_e) {
            rect = null;
        }
        return { query: q, atNode: container, atOffset: atIdx, caretOffset: offset, rect };
    }, []);

    const handleInput = useCallback(async () => {
        if (skipNextInput.current) { skipNextInput.current = false; return; }
        emit();
        const det = detectAt();
        if (!det) { setDd(null); return; }
        // Empty query or trailing double-space closes dropdown
        if (det.query === "" || det.query.endsWith("  ")) { setDd(null); return; }
        const seq = ++resolveSeq.current;
        try {
            const r = await resolveMention(det.query, space, version);
            // Race guard: only apply latest
            if (seq !== resolveSeq.current) return;
            setActiveIdx(0);
            setDd({ query: det.query, results: r, rect: det.rect });
        } catch (_e) {
            if (seq === resolveSeq.current) setDd(null);
        }
    }, [detectAt, emit, space, version]);

    // Close on outside click
    useEffect(() => {
        const h = (e) => {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target) &&
                !e.target.closest?.("[data-testid='mention-dropdown']")) {
                setDd(null);
            }
        };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    // ---------- Insertion ----------

    const insertNode = useCallback(async (item) => {
        // Re-detect at position FRESH so we replace the current @-query, not stale offsets.
        const det = detectAt();
        if (!det) return;
        let nodeData;
        if (item.kind === "verse") {
            nodeData = {
                type: "verse_card",
                node_id: uid(),
                reference: item.reference,
                label: item.label,
                translation: item.translation || version,
                text: item.text || "",
            };
        } else if (item.kind === "subject") {
            nodeData = {
                type: "mention",
                node_id: uid(),
                subject_id: item.subject_id,
                name: item.name,
            };
        } else if (item.kind === "create") {
            try {
                const s = await createSubject(item.name);
                onSubjectCreated?.(s);
                nodeData = {
                    type: "mention",
                    node_id: uid(),
                    subject_id: s.id,
                    name: s.name,
                };
            } catch (_e) {
                return;
            }
        } else {
            return;
        }

        // Replace text from '@' up to caret with the new element
        const textNode = det.atNode;
        const before = textNode.textContent.slice(0, det.atOffset);
        const after = textNode.textContent.slice(det.caretOffset);
        textNode.textContent = before;

        const el = nodeData.type === "mention" ? makeMentionElem(nodeData) : makeVerseCardElem(nodeData);
        const trail = document.createTextNode("\u00A0" + after);
        textNode.parentNode.insertBefore(el, textNode.nextSibling);
        el.parentNode.insertBefore(trail, el.nextSibling);

        // caret after inserted (position 1: after the &nbsp;)
        const sel = window.getSelection();
        const range = document.createRange();
        range.setStart(trail, 1);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);

        setDd(null);
        resolveSeq.current++; // invalidate any in-flight results
        skipNextInput.current = true;
        emit();
    }, [detectAt, version, onSubjectCreated, emit]);

    // ---------- Keyboard ----------

    const handleKeyDown = useCallback((e) => {
        if (!dd) return;
        const total = (dd.results?.scripture?.length || 0) + (dd.results?.people?.length || 0);
        if (!total) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => (i + 1) % total);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => (i - 1 + total) % total);
        } else if (e.key === "Enter") {
            e.preventDefault();
            const flat = [...(dd.results.scripture || []), ...(dd.results.people || [])];
            const item = flat[activeIdx];
            if (item) insertNode(item);
        } else if (e.key === "Escape") {
            e.preventDefault();
            setDd(null);
        }
    }, [dd, activeIdx, insertNode]);

    return (
        <div className="relative">
            <div
                ref={rootRef}
                data-testid="mention-editor"
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                spellCheck
                aria-label="Editor"
                className="min-h-[220px] outline-none scripture text-[17px] leading-[1.8] text-ink"
                data-placeholder={placeholder}
            />
            {dd && (dd.results?.scripture?.length || dd.results?.people?.length) ? (
                <MentionDropdown
                    dd={dd}
                    activeIdx={activeIdx}
                    onPick={insertNode}
                    space={space}
                />
            ) : null}
        </div>
    );
}

// ---------- Dropdown ----------

function MentionDropdown({ dd, activeIdx, onPick, space }) {
    const scripture = dd.results?.scripture || [];
    const people = dd.results?.people || [];
    const scriptureLabel = dd.results?.scripture_label || "Scripture";
    let idx = -1;
    return (
        <div
            data-testid="mention-dropdown"
            className="absolute z-40 left-0 right-0 mt-1 mx-auto max-w-md bg-white rounded-2xl border border-ink-10 shadow-lg overflow-hidden"
            style={{ top: "100%" }}
        >
            <div className="max-h-[60vh] overflow-y-auto sheet-scroll">
                {scripture.length > 0 && (
                    <>
                        <div className="px-4 pt-3 pb-1 font-ui text-[10px] uppercase tracking-[0.18em] text-ink-40">
                            Searching: {scriptureLabel}
                        </div>
                        <ul>
                            {scripture.map((it) => {
                                idx += 1;
                                const active = idx === activeIdx;
                                return (
                                    <li key={`s-${idx}`}>
                                        <button
                                            type="button"
                                            data-testid={`mention-item-scripture-${it.reference}`}
                                            onMouseDown={(e) => { e.preventDefault(); onPick(it); }}
                                            className={
                                                "w-full text-left px-4 py-2.5 transition " +
                                                (active ? "bg-moss/10" : "hover:bg-sage-paper")
                                            }
                                        >
                                            <div className="font-ui text-[11px] uppercase tracking-[0.18em] text-moss-deep font-semibold">
                                                {it.label}
                                            </div>
                                            <div className="scripture text-[14px] text-ink mt-0.5 line-clamp-2">
                                                {it.text}
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}
                {space === "journal" && people.length > 0 && (
                    <>
                        <div className={"px-4 pt-3 pb-1 font-ui text-[10px] uppercase tracking-[0.18em] text-ink-40 " + (scripture.length ? "border-t border-ink-10 mt-1" : "")}>
                            People
                        </div>
                        <ul className="pb-2">
                            {people.map((it) => {
                                idx += 1;
                                const active = idx === activeIdx;
                                const isCreate = it.kind === "create";
                                return (
                                    <li key={`p-${idx}`}>
                                        <button
                                            type="button"
                                            data-testid={isCreate ? `mention-item-add-${it.name}` : `mention-item-person-${it.subject_id}`}
                                            onMouseDown={(e) => { e.preventDefault(); onPick(it); }}
                                            className={
                                                "w-full text-left px-4 py-2.5 transition " +
                                                (active ? "bg-plum/10" : "hover:bg-sage-paper")
                                            }
                                        >
                                            {isCreate ? (
                                                <div className="font-ui text-[13px] text-plum">
                                                    + Add “{it.name}” to praying-for list
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <span className="font-ui text-[13px] font-medium text-ink">{it.name}</span>
                                                    <span className={
                                                        "font-ui text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded " +
                                                        (it.status === "answered"
                                                            ? "bg-moss/15 text-moss-deep"
                                                            : "bg-plum/10 text-plum")
                                                    }>
                                                        {it.status === "answered" ? "answered" : "praying"}
                                                    </span>
                                                </div>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}
                {!scripture.length && !people.length && (
                    <div className="px-4 py-3 font-ui text-sm text-ink-40">No matches yet — keep typing.</div>
                )}
            </div>
        </div>
    );
}
